import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { AppWindow } from "lucide-react";
import api from "@/lib/api";

/**
 * TopBarPreview — Iter 111 + Iter 113.
 *
 * Miniature mock of the app top-bar that reflects a candidate logo
 * upload BEFORE the admin saves. Given `lightSrc` and `darkSrc`
 * (either data-URLs from a fresh file pick or existing URLs), it
 * renders two stacked strips — light-mode chrome on top, dark-mode
 * chrome below — so admins can eyeball contrast + trim in both
 * themes at once.
 *
 * Iter 113 — When `primaryColor` is supplied, we also render:
 *   1. A colour swatch chip with the hex value
 *   2. A demo icon strip using the first 5 apps visible to the
 *      current viewer, tinted with the candidate colour so admins
 *      can see how the brand colour paints app icons before saving.
 *
 * Falls back to the shipped Conduit v2 default when a variant is
 * unset. Keep the strip tiny so it doesn't dominate the settings
 * form. Renders 220px wide by default.
 */

function DynIcon({ name, className, style }) {
  const C = Icons[name] || AppWindow;
  return <C className={className} style={style} />;
}

// Fallback icon set (rendered when /api/apps returns nothing — e.g.
// while loading, or for a viewer with no visible apps).
const FALLBACK_ICONS = ["Phone", "BarChart3", "Users", "FileText", "Settings"];

export function TopBarPreview({
  lightSrc,
  darkSrc,
  brandName = "iplogin",
  primaryColor = null,
  className = "",
}) {
  const light = lightSrc || "/brand/conduit-v2-light.png?v=3";
  const dark  = darkSrc  || "/brand/conduit-v2-dark.png?v=3";
  const showAccents = !!primaryColor;

  const [apps, setApps] = useState([]);
  useEffect(() => {
    if (!showAccents) return;
    let alive = true;
    api.get("/apps").then((r) => {
      if (!alive) return;
      const rows = (Array.isArray(r.data) ? r.data : [])
        .filter((a) => a.icon)              // must have a Lucide icon
        .slice(0, 5);
      setApps(rows);
    }).catch(() => {});
    return () => { alive = false; };
  }, [showAccents]);

  const iconRow = apps.length > 0
    ? apps.map((a, i) => ({ key: a.app_id || i, icon: a.icon, name: a.name }))
    : FALLBACK_ICONS.map((n, i) => ({ key: `fb-${i}`, icon: n, name: n }));

  return (
    <div className={`space-y-2 ${className}`} data-testid="topbar-preview">
      <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">Header preview</div>
      <div className="overflow-hidden rounded-md border border-slate-200 shadow-sm">
        {/* Light-mode strip */}
        <div className="flex h-9 items-center gap-2 border-b border-slate-100 bg-white px-2" data-testid="topbar-preview-light">
          <img src={light} alt="light preview" className="h-6 w-auto object-contain" />
          <span className="rounded-md bg-amber-500 px-1 py-0 text-[7px] font-bold uppercase tracking-widest text-white">Beta</span>
          <div className="ml-auto flex items-center gap-1">
            <span className="h-1 w-8 rounded bg-slate-100" />
            <span className="h-1 w-6 rounded bg-slate-100" />
            <span className="h-4 w-4 rounded-full bg-slate-200" />
          </div>
        </div>
        {/* Dark-mode strip */}
        <div className="flex h-9 items-center gap-2 bg-slate-900 px-2" data-testid="topbar-preview-dark">
          <img src={dark} alt="dark preview" className="h-6 w-auto object-contain" />
          <span className="rounded-md bg-amber-500 px-1 py-0 text-[7px] font-bold uppercase tracking-widest text-white">Beta</span>
          <div className="ml-auto flex items-center gap-1">
            <span className="h-1 w-8 rounded bg-slate-700" />
            <span className="h-1 w-6 rounded bg-slate-700" />
            <span className="h-4 w-4 rounded-full bg-slate-700" />
          </div>
        </div>
      </div>

      {showAccents && (
        <div className="space-y-1.5" data-testid="topbar-preview-accents">
          <div className="flex items-center gap-2" data-testid="topbar-preview-color-swatch">
            <div
              className="h-5 w-5 rounded-full border border-slate-200 shadow-sm"
              style={{ backgroundColor: primaryColor }}
            />
            <code className="rounded bg-slate-50 px-1.5 py-0.5 text-[10px] font-mono font-medium text-slate-600">
              {String(primaryColor).toUpperCase()}
            </code>
            <span className="text-[10px] text-slate-400">Primary colour tints app icons →</span>
          </div>
          <div
            className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2"
            data-testid="topbar-preview-icons"
          >
            {iconRow.map((it) => (
              <div
                key={it.key}
                title={it.name}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-100 bg-slate-50"
                data-testid={`topbar-preview-icon-${it.icon}`}
              >
                <DynIcon name={it.icon} className="h-4 w-4" style={{ color: primaryColor }} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-slate-400">Simulates the real header. Save to apply.</div>
    </div>
  );
}
